import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { fileUrl } from "@/lib/api";

const ROLES = ["employee", "manager", "po_admin", "super_admin"];
const STATUSES = ["active", "on_leave", "separated"];

const roleLevel = { employee: 1, manager: 2, po_admin: 3, super_admin: 4 };

export default function Directory() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [campusId, setCampusId] = useState("");
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const isManagerPlus = (roleLevel[user?.role || "employee"] || 1) >= 2;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: c } = await api.get("/campuses");
        if (mounted) setCampuses(c);
      } catch {
        /* ignore */
      }
    })();
    return () => (mounted = false);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (q) params.q = q;
        if (campusId) params.campus_id = campusId;
        if (department) params.department = department;
        if (role) params.role = role;
        if (status) params.employment_status = status;
        const { data } = await api.get("/users/directory", { params });
        setUsers(data);
      } finally {
        setLoading(false);
      }
    };
    const iv = setTimeout(load, 200);
    return () => clearTimeout(iv);
  }, [q, campusId, department, role, status]);

  const campusName = (id) => campuses.find((c) => c.id === id)?.name || "—";

  const departments = useMemo(() => {
    const set = new Set();
    users.forEach((u) => u.department && set.add(u.department));
    return Array.from(set).sort();
  }, [users]);

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">People</p>
          <h1 className="text-4xl font-serif text-forest-900 mt-1">Directory</h1>
          <p className="mt-2 text-sm text-charcoal-soft">
            {isManagerPlus
              ? "Complete listing of the Patria team across every campus."
              : "The Patria team across our campuses."}
          </p>
        </div>
        <span className="patria-chip-green" data-testid="directory-count">
          {users.length} team members
        </span>
      </div>

      <div className="patria-card p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          <div className="sm:col-span-2 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-muted"
              strokeWidth={1.5}
            />
            <input
              placeholder="Search by name, email, title..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="patria-input w-full pl-9"
              data-testid="directory-search"
            />
          </div>
          <select
            className="patria-input"
            value={campusId}
            onChange={(e) => setCampusId(e.target.value)}
            data-testid="directory-filter-campus"
          >
            <option value="">All campuses</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            className="patria-input"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            data-testid="directory-filter-department"
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          {isManagerPlus && (
            <select
              className="patria-input"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="directory-filter-role"
            >
              <option value="">All roles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.replace("_", " ")}
                </option>
              ))}
            </select>
          )}
          {isManagerPlus && (
            <select
              className="patria-input sm:col-start-5"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="directory-filter-status"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-hidden rounded-2xl shadow-card">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th>Name</th>
              {isManagerPlus && <th>Email</th>}
              <th>Title</th>
              <th>Campus</th>
              <th>Department</th>
              {isManagerPlus && <th>Role</th>}
              {isManagerPlus && <th>Status</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="italic text-charcoal-muted">
                  Loading team...
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={7} className="italic text-charcoal-muted" data-testid="directory-empty">
                  No teammates match those filters.
                </td>
              </tr>
            )}
            {!loading &&
              users.map((u) => (
                <tr key={u.id} data-testid={`directory-row-${u.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      {u.avatar_path ? (
                        <img
                          src={fileUrl(u.avatar_path)}
                          alt={u.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <span className="h-8 w-8 rounded-full bg-forest-600/10 text-forest-700 flex items-center justify-center text-xs font-semibold">
                          {u.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </span>
                      )}
                      <span className="font-medium text-forest-900">{u.name}</span>
                    </div>
                  </td>
                  {isManagerPlus && <td className="text-charcoal-soft">{u.email}</td>}
                  <td>{u.job_title || "—"}</td>
                  <td>{campusName(u.campus_id)}</td>
                  <td>{u.department || "—"}</td>
                  {isManagerPlus && (
                    <td>
                      <span className="patria-chip-green capitalize">
                        {(u.role || "employee").replace("_", " ")}
                      </span>
                    </td>
                  )}
                  {isManagerPlus && (
                    <td>
                      <span
                        className={
                          u.employment_status === "active"
                            ? "patria-chip-green capitalize"
                            : "patria-chip-terra capitalize"
                        }
                      >
                        {(u.employment_status || "").replace("_", " ")}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {users.map((u) => (
          <div key={u.id} className="patria-card p-4">
            <div className="flex items-center gap-3">
              {u.avatar_path ? (
                <img
                  src={fileUrl(u.avatar_path)}
                  alt={u.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <span className="h-10 w-10 rounded-full bg-forest-600/10 text-forest-700 flex items-center justify-center text-xs font-semibold">
                  {u.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </span>
              )}
              <div className="min-w-0">
                <p className="font-medium text-forest-900 truncate">{u.name}</p>
                <p className="text-xs text-charcoal-muted truncate">{u.job_title}</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-charcoal-soft">
              {campusName(u.campus_id)} · {u.department || "—"}
            </p>
            {isManagerPlus && (
              <p className="mt-1 text-xs text-charcoal-muted">{u.email}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
